const STRIPE_API_BASE = "/api/stripe"
const DAILY_LIMIT = 5
const CREDIT_KEY = "tts-credits"
const PURCHASED_KEY = "tts-purchased-credits"
const USER_ID_KEY = "tts-user-id"
const SUBSCRIPTION_KEY = "tts-subscription"
const DAILY_TOKEN_KEY = "tts-daily-token"

export function getUserId(): string {
    let id = localStorage.getItem(USER_ID_KEY)
    if (!id) {
        id = "user_" + Math.random().toString(36).slice(2) + Date.now().toString(36)
        localStorage.setItem(USER_ID_KEY, id)
    }
    return id
}

function getDailyCredits(): {count: number, date: string} {
    const today = new Date().toISOString().slice(0, 10)
    try {
        const raw = localStorage.getItem(CREDIT_KEY)
        if (raw) {
            const data = JSON.parse(raw)
            if (data.date === today) return {count: data.count, date: today}
        }
    } catch {}
    return {count: 0, date: today}
}

export function useDailyCredit(): number {
    const credits = getDailyCredits()
    credits.count++
    localStorage.setItem(CREDIT_KEY, JSON.stringify(credits))
    return DAILY_LIMIT - credits.count
}

export function remainingDailyCredits(): number {
    return DAILY_LIMIT - getDailyCredits().count
}

export function getPurchasedCredits(): number {
    try {
        const raw = localStorage.getItem(PURCHASED_KEY)
        return raw ? parseInt(raw, 10) : 0
    } catch {
        return 0
    }
}

export function setPurchasedCredits(n: number): void {
    localStorage.setItem(PURCHASED_KEY, String(Math.max(0, n)))
    notifyListeners()
}

export function usePurchasedCredit(): boolean {
    const current = getPurchasedCredits()
    if (current <= 0) return false
    setPurchasedCredits(current - 1)
    return true
}

export function totalRemaining(): number {
    if (isUnlimitedSubscriber()) return 999
    return remainingDailyCredits() + getPurchasedCredits()
}

export function isUnlimitedSubscriber(): boolean {
    try {
        const raw = localStorage.getItem(SUBSCRIPTION_KEY)
        if (!raw) return false
        const data = JSON.parse(raw)
        return data.active === true
    } catch {
        return false
    }
}

export function setUnlimitedSubscription(active: boolean): void {
    localStorage.setItem(SUBSCRIPTION_KEY, JSON.stringify({active}))
    notifyListeners()
}

export function getDailyLimit(): number {
    return DAILY_LIMIT
}

export function useCredit(): boolean {
    if (isUnlimitedSubscriber()) return true
    const daily = remainingDailyCredits()
    if (daily > 0) {
        useDailyCredit()
        notifyListeners()
        return true
    }
    const purchased = getPurchasedCredits()
    if (purchased > 0) {
        usePurchasedCredit()
        return true
    }
    return false
}

export function getCreditsInfo(): {daily: number, purchased: number, unlimited: boolean, total: number, dailyLimit: number} {
    const daily = remainingDailyCredits()
    const purchased = getPurchasedCredits()
    const unlimited = isUnlimitedSubscriber()
    return {daily, purchased, unlimited, total: unlimited ? 999 : daily + purchased, dailyLimit: DAILY_LIMIT}
}

// Listener system for UI updates
type CreditListener = () => void
const listeners: Set<CreditListener> = new Set()

export function onCreditsChange(listener: CreditListener): () => void {
    listeners.add(listener)
    return () => listeners.delete(listener)
}

function notifyListeners(): void {
    for (const l of listeners) l()
}

export async function getDailyToken(): Promise<string> {
    const today = new Date().toISOString().slice(0, 10)
    try {
        const raw = localStorage.getItem(DAILY_TOKEN_KEY)
        if (raw) {
            const cached = JSON.parse(raw)
            if (cached.date === today && cached.token) return cached.token as string
        }
    } catch {}
    const userId = getUserId()
    try {
        const resp = await fetch("/api/credits/token", {
            method: "POST",
            headers: {"Content-Type": "application/json"},
            body: JSON.stringify({userId})
        })
        if (resp.ok) {
            const data = await resp.json()
            if (data.token) {
                localStorage.setItem(DAILY_TOKEN_KEY, JSON.stringify({date: today, token: data.token}))
                return data.token as string
            }
        }
    } catch {}
    return ""
}

// Stripe checkout
const CREDIT_PACKS = [
    {id: "pack_10", credits: 10, price: "$5.00", label: "10 Credits", perCredit: "$0.50 / credit"},
    {id: "pack_50", credits: 50, price: "$25.00", label: "50 Credits", perCredit: "$0.50 / credit", badge: "Popular"},
    {id: "pack_100", credits: 100, price: "$50.00", label: "100 Credits", perCredit: "$0.50 / credit", badge: "Best Deal"}
]

export {CREDIT_PACKS, STRIPE_API_BASE}

export async function createCheckoutSession(packId: string): Promise<string> {
    const userId = getUserId()
    const resp = await fetch(`${STRIPE_API_BASE}/create-checkout-session`, {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({packId, userId})
    })
    const data = await resp.json()
    if (data.url) return data.url
    throw new Error(data.error || "Failed to create checkout session")
}

// Payment return handling
export async function handlePaymentReturn(): Promise<{success: boolean, credits: number} | null> {
    const params = new URLSearchParams(window.location.search)
    const paymentStatus = params.get("payment")
    const sessionId = params.get("session_id")
    const packId = params.get("pack")

    if (paymentStatus === "success" && sessionId) {
        try {
            const resp = await fetch(`${STRIPE_API_BASE}/verify-session/${sessionId}`)
            if (resp.ok) {
                const data = await resp.json()
                if (data.status === "paid") {
                    const granted = data.credits > 0 ? data.credits : 0
                    if (packId === "unlimited") {
                        setUnlimitedSubscription(true)
                    } else if (granted > 0) {
                        const current = getPurchasedCredits()
                        setPurchasedCredits(current + granted)
                    }
                    cleanUrlParams()
                    return {success: true, credits: granted}
                }
            }
        } catch (e) {
            console.warn("Could not verify payment session:", e)
        }
        cleanUrlParams()
    } else if (paymentStatus === "cancelled") {
        cleanUrlParams()
    }
    return null
}

// Analytics tracking helpers
export function trackAnalyticsPageView(): void {
    const userId = getUserId()
    fetch("/api/analytics/pageview", {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({userId})
    }).catch(() => {})
}

export function trackAnalyticsApiCall(endpoint: string, model: string): void {
    const userId = getUserId()
    fetch("/api/analytics/track-api", {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({userId, endpoint, model})
    }).catch(() => {})
}

// Fire page view on import
trackAnalyticsPageView()

function cleanUrlParams(): void {
    const url = new URL(window.location.href)
    url.searchParams.delete("payment")
    url.searchParams.delete("session_id")
    url.searchParams.delete("credits")
    url.searchParams.delete("pack")
    window.history.replaceState({}, "", url.toString())
}
